export const NOTICE_UPLOAD_IN_PROGRESS_MESSAGE =
  "Please wait for uploads to finish before continuing.";

export class PresignedUploadAbortError extends Error {
  constructor() {
    super("Upload cancelled");
    this.name = "PresignedUploadAbortError";
  }
}

export type PresignedUploadProgress = {
  loaded: number;
  total: number;
  percent: number;
};

/** PUT to S3 presigned URL with upload progress (fetch cannot report %). */
export function uploadFileToPresignedUrl(params: {
  presignedUrl: string;
  file: Blob;
  contentType: string;
  onProgress?: (progress: PresignedUploadProgress) => void;
  signal?: AbortSignal;
}): Promise<void> {
  const { presignedUrl, file, contentType, onProgress, signal } = params;

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", presignedUrl);
    xhr.setRequestHeader("Content-Type", contentType);

    const cleanup = () => {
      signal?.removeEventListener("abort", onAbort);
    };

    const onAbort = () => {
      xhr.abort();
    };

    if (signal) {
      if (signal.aborted) {
        onAbort();
        reject(new PresignedUploadAbortError());
        return;
      }
      signal.addEventListener("abort", onAbort, { once: true });
    }

    xhr.upload.onprogress = (event) => {
      if (!onProgress) return;
      const total = event.lengthComputable ? event.total : file.size;
      const loaded = event.loaded;
      const percent =
        total > 0 ? Math.min(100, Math.round((loaded / total) * 100)) : 0;
      onProgress({ loaded, total, percent });
    };

    xhr.onload = () => {
      cleanup();
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
        return;
      }
      reject(
        new Error(
          `Failed to upload file to S3: ${xhr.statusText || String(xhr.status)}`
        )
      );
    };

    xhr.onerror = () => {
      cleanup();
      reject(new Error("Network error while uploading file"));
    };

    xhr.onabort = () => {
      cleanup();
      reject(new PresignedUploadAbortError());
    };

    xhr.send(file);
  });
}

export type NoticeUploadProgressState = {
  percent: number;
  fileName: string;
  fileIndex: number;
  fileCount: number;
};

export function formatNoticeUploadStatus(
  fileName: string,
  percent: number,
  fileIndex: number,
  fileCount: number
): string {
  const shortName =
    fileName.length > 32 ? `${fileName.slice(0, 29)}…` : fileName;
  const batch =
    fileCount > 1 ? ` (${fileIndex}/${fileCount})` : "";
  return `Uploading ${shortName}${batch}… ${percent}%`;
}

export type PresignedUploadedFile = {
  fileUrl: string;
  fileName: string;
  contentType: string;
  size: number;
};

export type PresignedUploadTarget = {
  presignedUrl: string;
  fileUrl: string;
  fileName: string;
  contentType: string;
};

/** Sequential presigned uploads with progress reporting and cancel support. */
export async function uploadFilesWithPresignedProgress(params: {
  files: File[];
  signal?: AbortSignal;
  onProgress?: (progress: NoticeUploadProgressState) => void;
  onStatusMessage?: (message: string) => void;
  prepareFile?: (file: File) => Promise<File>;
  resolveUploadTarget: (
    file: File,
    index: number
  ) => Promise<PresignedUploadTarget>;
}): Promise<PresignedUploadedFile[]> {
  const {
    files,
    signal,
    onProgress,
    onStatusMessage,
    prepareFile,
    resolveUploadTarget,
  } = params;

  const fileCount = files.length;
  const uploaded: PresignedUploadedFile[] = [];

  for (let fileIndex = 0; fileIndex < fileCount; fileIndex++) {
    if (signal?.aborted) {
      throw new PresignedUploadAbortError();
    }

    let file = files[fileIndex];
    if (prepareFile) {
      file = await prepareFile(file);
    }

    const reportProgress = (percent: number) => {
      const progress: NoticeUploadProgressState = {
        percent,
        fileName: file.name,
        fileIndex: fileIndex + 1,
        fileCount,
      };
      onProgress?.(progress);
      onStatusMessage?.(
        formatNoticeUploadStatus(file.name, percent, fileIndex + 1, fileCount)
      );
    };

    reportProgress(0);

    const target = await resolveUploadTarget(file, fileIndex);

    if (signal?.aborted) {
      throw new PresignedUploadAbortError();
    }

    await uploadFileToPresignedUrl({
      presignedUrl: target.presignedUrl,
      file,
      contentType: target.contentType,
      signal,
      onProgress: ({ percent }) => reportProgress(percent),
    });

    uploaded.push({
      fileUrl: target.fileUrl,
      fileName: target.fileName,
      contentType: target.contentType,
      size: file.size,
    });
  }

  return uploaded;
}
